import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Upload, AlertTriangle } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea, Switch, Checkbox } from '@/components/ui/misc'
import { toast } from 'sonner'
import { api, apiError } from '@/lib/api-client'
import { currentLocale } from '@/lib/i18n'
import { useZaloAccounts } from '@/hooks/use-integrations'
import { useTaoChienDich } from '@/hooks/use-zalo-friend-campaigns'

/** Zalo cắt lời mời kết bạn quá dài — giữ dưới mức này. */
const TOI_DA_LOI_MOI = 130
/** Trần cứng trong zalo-policy.ts. Ô nhập không cho vượt. */
const TRAN_CUNG = 50

interface DongCsv {
  phone: string
  ten?: string
  tai_khoan?: string
  thu_tu?: number
}

/**
 * Đọc CSV xuất từ script dựng chiến dịch (CD2_Zalo_1-1_ZL01..10.csv).
 * Chỉ cần cột `phone`; `ten`/`tai_khoan`/`thu_tu` có thì dùng, không có thì bỏ qua.
 */
function docCsv(text: string): DongCsv[] {
  // Tên cột khớp đúng định dạng file CSV ngoài (CD2_Zalo_1-1_ZL01..10.csv) — không phải chuỗi UI, giữ nguyên.
  const dong = text.split(/\r?\n/).filter((d) => d.trim()) // i18n-ignore
  if (dong.length < 2) return []
  const cot = dong[0].split(',').map((c) => c.trim().toLowerCase())
  const iPhone = cot.findIndex((c) => c === 'phone' || c === 'sdt' || c.includes('điện thoại')) // i18n-ignore
  if (iPhone < 0) return []
  const iTen = cot.findIndex((c) => c === 'ten' || c === 'displayname' || c === 'name')
  const iTk = cot.findIndex((c) => c === 'tai_khoan' || c === 'taikhoan')
  const iThuTu = cot.findIndex((c) => c === 'thu_tu' || c === 'orderindex' || c === 'luy_ke_trong_tk')

  return dong.slice(1).map((d) => {
    const o = d.split(',')
    const r: DongCsv = { phone: (o[iPhone] ?? '').trim() }
    if (iTen >= 0) r.ten = (o[iTen] ?? '').trim()
    if (iTk >= 0) r.tai_khoan = (o[iTk] ?? '').trim()
    if (iThuTu >= 0) {
      const n = Number((o[iThuTu] ?? '').trim())
      if (Number.isFinite(n)) r.thu_tu = n
    }
    return r
  }).filter((r) => r.phone)
}

export function CreateFriendCampaignDialog({
  open, onOpenChange,
}: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const navigate = useNavigate()
  const { t } = useTranslation('marketing')
  const { data: taiKhoan = [] } = useZaloAccounts('personal')
  const tao = useTaoChienDich()
  const fileRef = useRef<HTMLInputElement>(null)

  const [ten, setTen] = useState('')
  // Nội dung mặc định GỬI THẲNG CHO KHÁCH qua Zalo (lời mời kết bạn + tin chào) — dữ liệu gửi khách, không phải
  // chuỗi UI, giữ tiếng Việt bất kể ngôn ngữ giao diện người soạn đang chọn. Người soạn tự sửa trước khi chạy.
  const [loiMoi, setLoiMoi] = useState(
    'Chào anh/chị, em là CSKH riêng của Goya ạ. Em kết bạn để hỗ trợ anh/chị đổi size & gửi ưu đãi hội viên riêng.', // i18n-ignore
  )
  const [tin1, setTin1] = useState('Em cảm ơn anh/chị {Ten} đã nhận kết bạn ạ. Em là đầu mối CSKH riêng của anh/chị ở Goya — cần đổi size, bảo hành hay tư vấn mẫu thì nhắn thẳng em nhé.') // i18n-ignore
  const [tin2, setTin2] = useState('')
  const [tuDongChao, setTuDongChao] = useState(true)
  const [hanMuc, setHanMuc] = useState(TRAN_CUNG)
  const [warmup, setWarmup] = useState(true)
  const [gioBatDau, setGioBatDau] = useState(8)
  const [gioKetThuc, setGioKetThuc] = useState(20)
  const [chon, setChon] = useState<string[]>([])
  const [rows, setRows] = useState<DongCsv[]>([])
  const [tenFile, setTenFile] = useState('')
  const [dangLuu, setDangLuu] = useState(false)

  const quaDai = loiMoi.length > TOI_DA_LOI_MOI
  const hopLe = ten.trim() && loiMoi.trim() && chon.length > 0 && rows.length > 0

  const moiTaiKhoan = useMemo(
    () => (chon.length > 0 ? Math.ceil(rows.length / chon.length) : 0),
    [rows.length, chon.length],
  )

  async function chonFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return
    const parsed = docCsv(await f.text())
    if (parsed.length === 0) {
      toast.error(t('friendCreate.csvReadError'))
      return
    }
    setRows(parsed)
    setTenFile(f.name)
    toast.success(t('friendCreate.csvReadToast', { count: parsed.length.toLocaleString(currentLocale()) }))
  }

  async function luu() {
    setDangLuu(true)
    try {
      const cd: any = await tao.mutateAsync({
        name: ten.trim(),
        inviteMessage: loiMoi.trim(),
        welcomeMessages: [tin1, tin2].map((t) => t.trim()).filter(Boolean),
        autoWelcome: tuDongChao,
        dailyLimit: hanMuc,
        warmupEnabled: warmup,
        sendWindowStart: gioBatDau,
        sendWindowEnd: gioKetThuc,
      } as any)

      // Nạp tệp phải gọi SAU khi đã có id chiến dịch.
      const { data: kq } = await api.post<{ them: number; boQua: number; tong: number }>(
        `/zalo-friend-campaigns/${cd.id}/targets/import`,
        { rows, accountIds: chon },
      )
      const { them, boQua } = kq

      toast.success(t('friendCreate.createdToast', { imported: them.toLocaleString(currentLocale()), skipped: boQua }))
      onOpenChange(false)
      navigate(`/zalo-friend-campaigns/${cd.id}`)
    } catch (e) {
      toast.error(apiError(e))
    } finally {
      setDangLuu(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('friendCreate.title')}</DialogTitle>
          <DialogDescription>
            {t('friendCreate.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label>{t('friendCreate.nameLabel')}</Label>
            <Input value={ten} onChange={(e) => setTen(e.target.value)}
              placeholder={t('friendCreate.namePlaceholder')} />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t('friendCreate.inviteLabel')}</Label>
              <span className={quaDai ? 'text-xs font-medium text-destructive' : 'text-xs text-muted-foreground'}>
                {loiMoi.length}/{TOI_DA_LOI_MOI}
              </span>
            </div>
            <Textarea rows={3} value={loiMoi} onChange={(e) => setLoiMoi(e.target.value)} />
            {quaDai && (
              <p className="flex items-center gap-1 text-xs text-destructive">
                <AlertTriangle className="h-3 w-3" />
                {t('friendCreate.inviteTooLong', { max: TOI_DA_LOI_MOI })}
              </p>
            )}
          </div>

          <div className="space-y-3 rounded-lg border p-3">
            <div className="flex items-center justify-between">
              <Label>{t('friendCreate.autoWelcomeLabel')}</Label>
              <Switch checked={tuDongChao} onCheckedChange={setTuDongChao} />
            </div>
            {tuDongChao && (
              <>
                <Textarea rows={2} value={tin1} onChange={(e) => setTin1(e.target.value)}
                  placeholder={t('friendCreate.welcome1Placeholder')} />
                <Textarea rows={2} value={tin2} onChange={(e) => setTin2(e.target.value)}
                  placeholder={t('friendCreate.welcome2Placeholder')} />
                <p className="text-xs text-muted-foreground">
                  {t('friendCreate.welcomeLimitHint')}
                </p>
              </>
            )}
          </div>

          <div className="space-y-2">
            <Label>{t('friendCreate.accountsLabel', { count: chon.length })}</Label>
            {taiKhoan.length === 0 ? (
              <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                {t('friendCreate.noAccountsBefore')} <strong>{t('friendCreate.noAccountsIntegrations')}</strong> {t('friendCreate.noAccountsAfter')}
              </p>
            ) : (
              <div className="grid max-h-44 grid-cols-2 gap-2 overflow-y-auto rounded-md border p-2">
                {taiKhoan.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted">
                    <Checkbox
                      checked={chon.includes(a.id)}
                      onCheckedChange={(v) =>
                        setChon((s) => (v ? [...s, a.id] : s.filter((x) => x !== a.id)))
                      }
                    />
                    <span className="truncate">{a.displayName ?? a.phone ?? a.id.slice(0, 8)}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>{t('friendCreate.csvLabel')}</Label>
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}>
                <Upload className="h-4 w-4" /> {t('friendCreate.chooseFile')}
              </Button>
              <span className="text-sm text-muted-foreground">
                {tenFile ? t('friendCreate.fileSummary', { name: tenFile, rows: rows.length.toLocaleString(currentLocale()) }) : t('friendCreate.noFileChosen')}
              </span>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={chonFile} />
            </div>
            <p className="text-xs text-muted-foreground">
              {t('friendCreate.csvColumnsBefore')} <code>phone</code>. {t('friendCreate.csvColumnsMid')} <code>ten</code>, <code>tai_khoan</code>,{' '}
              <code>thu_tu</code> {t('friendCreate.csvColumnsAfter')}
            </p>
            {rows.length > 0 && chon.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {t('friendCreate.splitHint')} <strong>{t('friendCreate.splitCount', { count: moiTaiKhoan.toLocaleString(currentLocale()) })}</strong>
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>{t('friendCreate.dailyLimitLabel')}</Label>
              <Input
                type="number" min={1} max={TRAN_CUNG} value={hanMuc}
                onChange={(e) => setHanMuc(Math.max(1, Math.min(TRAN_CUNG, Number(e.target.value) || 1)))}
              />
              <p className="text-xs text-muted-foreground">{t('friendCreate.dailyLimitHint', { max: TRAN_CUNG })}</p>
            </div>
            <div className="space-y-2">
              <Label>{t('friendCreate.windowStartLabel')}</Label>
              <Input type="number" min={0} max={23} value={gioBatDau}
                onChange={(e) => setGioBatDau(Math.max(0, Math.min(23, Number(e.target.value) || 0)))} />
            </div>
            <div className="space-y-2">
              <Label>{t('friendCreate.windowEndLabel')}</Label>
              <Input type="number" min={1} max={24} value={gioKetThuc}
                onChange={(e) => setGioKetThuc(Math.max(1, Math.min(24, Number(e.target.value) || 24)))} />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label>{t('friendCreate.warmupLabel')}</Label>
              <p className="text-xs text-muted-foreground">
                {t('friendCreate.warmupHint')}
              </p>
            </div>
            <Switch checked={warmup} onCheckedChange={setWarmup} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('friendCreate.cancel')}</Button>
          <Button onClick={luu} disabled={!hopLe || dangLuu}>
            {dangLuu ? t('friendCreate.creating') : t('friendCreate.createButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
